export type CommunicationsSubdomain = 'api.in.exotel.com' | 'api.exotel.com';

export interface CommunicationsWebhooks {
  ivrUrl: string | null;
  statusCallbackUrl: string | null;
}

export interface InteraktSettingsPreview {
  enabled: boolean;
  configured: boolean;
}

export interface CommunicationsSettings {
  id?: string;
  ivrEnabled: boolean;
  whatsappLink: string | null;
  whatsappTemplateName: string | null;
  executivePhoneNumber: string | null;
  successVoiceMessage: string | null;
  failureVoiceMessage: string | null;
  isActive: boolean;
  updatedAt: string | null;
  apiKey: string | null;
  apiKeyConfigured: boolean;
  apiTokenConfigured: boolean;
  accountSid: string | null;
  apiSubdomain: CommunicationsSubdomain;
  exoPhone: string | null;
  webhooks?: CommunicationsWebhooks;
  interakt: InteraktSettingsPreview;
}

export interface CommunicationsActivityTotals {
  total: number;
  whatsappRequested: number;
  ivrDisabled: number;
  noAction: number;
  digitOne: number;
  otherDigits: number;
  outboundCalls: number;
  statusCallbacks: number;
  avgDurationSeconds: number;
  totalDurationSeconds: number;
}

export interface CommunicationsCallLogTotals {
  total: number;
  completed: number;
  inbound: number;
  outbound: number;
  avgDurationSeconds: number;
  totalDurationSeconds: number;
}

export interface CommunicationsIvrTotals {
  total: number;
  whatsappRequested: number;
  ivrDisabled: number;
  noAction: number;
  digitOne: number;
  otherDigits: number;
}

export interface CommunicationsActivityData {
  days: number;
  callLogs: {
    rows: CommunicationsActivityRow[];
    totals: CommunicationsCallLogTotals;
    source: 'exotel_api' | 'local';
    fetchError: string | null;
    meta?: { total?: number; pageSize?: number; source?: string };
  };
  ivrEvents: {
    rows: CommunicationsActivityRow[];
    totals: CommunicationsIvrTotals;
  };
}

export interface CommunicationsActivityRow {
  id: string;
  callSid: string | null;
  callStatus: string | null;
  fromNumber: string | null;
  toNumber: string | null;
  digits: string | null;
  direction: string | null;
  callType: string | null;
  eventType: string | null;
  actionTaken: string | null;
  duration: number | null;
  recordingUrl: string | null;
  price: string | number | null;
  startTime: string | null;
  endTime: string | null;
  source: string | null;
  createdAt: string;
}

export interface CommunicationsWebhookLog {
  id: string;
  callSid: string | null;
  callStatus: string | null;
  fromNumber: string | null;
  toNumber: string | null;
  eventType: string | null;
  actionTaken: string | null;
  source: string | null;
  duration: number | null;
  createdAt: string;
  rawPayload: Record<string, unknown> | null;
}

export interface ActiveCallItem {
  call_sid?: string;
  status?: string;
  from?: string;
  to?: string;
  start_time?: string;
  duration?: number;
  recording_enabled?: boolean;
}

export type CommunicationsTab = 'dashboard' | 'activity' | 'calls' | 'webhooks' | 'settings';

export const COMMUNICATIONS_BASE_PATH = '/operations/exotel-calling';

export const COMMUNICATIONS_TABS: { id: CommunicationsTab; label: string; path: string }[] = [
  { id: 'dashboard', label: 'Dashboard', path: `${COMMUNICATIONS_BASE_PATH}` },
  { id: 'activity', label: 'Call activity', path: `${COMMUNICATIONS_BASE_PATH}/activity` },
  { id: 'calls', label: 'Place a call', path: `${COMMUNICATIONS_BASE_PATH}/calls` },
  { id: 'webhooks', label: 'Webhook logs', path: `${COMMUNICATIONS_BASE_PATH}/webhooks` },
  { id: 'settings', label: 'Settings', path: `${COMMUNICATIONS_BASE_PATH}/settings` },
];

export interface CommunicationsDashboardData {
  days: number;
  totals: CommunicationsActivityTotals;
  bySource: Record<string, number>;
  byStatus: Record<string, number>;
  byAction: Record<string, number>;
  dailyVolume: { date: string; count: number }[];
  recentCalls: CommunicationsActivityRow[];
  setup: {
    ivrEnabled: boolean;
    apiConfigured: boolean;
    exoPhoneConfigured: boolean;
  };
  metrics: {
    completedWithDuration: number;
    answerRate: number;
  };
  activeCalls: {
    active_calls?: ActiveCallItem[];
    total_active?: number;
  };
}
